/**
 * A visitor-local IndexedDB cache for parsed safetensors inventories.
 *
 * **Best-effort by design.** If it works, the second visit to a repo is free;
 * if it does not, nothing is worse than before. That asymmetry is the whole
 * contract, and it is why every function here resolves a benign value rather
 * than rejecting — a cache is an optimisation, and an optimisation that can
 * take the product down with it has negative value however often it succeeds.
 * Read the rest of this comment with that framing: the elaborate failure
 * handling below is not defensive habit, it is the feature.
 *
 * Why this is safe rather than merely convenient: every entry is keyed on the
 * **immutable commit SHA**, so a hit can never be stale. The same property that
 * makes every payload URL CDN-cacheable (see `hub.ts`) makes a cached inventory
 * permanently correct — `(repoId, sha)` names one exact set of header bytes,
 * for all time. Keying on the repo id alone would serve one revision's tensor
 * list for another; that is the same class of bug the worker's `cacheKey`
 * comment warns about, and it is the one mistake this module must not make.
 *
 * What it buys: a sharded frontier model costs tens of seconds and a few
 * hundred KiB of Range requests to inventory, paid again by every visitor and
 * again by the same visitor tomorrow. This makes the second visit free — for
 * the browser and for the Hub.
 *
 * What it is *not*, and must never become: a shared cache. This is
 * `window.indexedDB` on one person's machine. There is no server, no edge, and
 * nothing here is redistributed. It introduces no network origin, so the CSP is
 * untouched; it never prefetches or crawls; and it can only avoid re-reading
 * something the visitor already asked for — it can never cause a repo to be
 * read that they did not ask for.
 *
 * **Every failure path degrades to "no cache", never to an error the visitor
 * sees.** IndexedDB is genuinely unavailable or broken in several ordinary
 * situations: Firefox private browsing (`open` errors, and has historically
 * thrown synchronously), Safari under storage restrictions, a quota-exceeded
 * write (the transaction aborts), a blocked upgrade while another tab holds an
 * older version open (`open` fires neither success nor error — it just hangs),
 * and a visitor who has switched site data off entirely. In every one of those
 * the app must behave exactly as it did before this module existed: fetch the
 * headers, say nothing. A caching layer that can break the product when it
 * fails is worse than no caching layer, so every entry point below resolves a
 * benign value instead of throwing, and the whole cache is switched off for the
 * session after the first failure.
 */

import type { Inventory } from "../safetensors/inventory.js";

const DB_NAME = "tensorlens-inventory-cache";
const STORE_DATA = "inventories";
const STORE_META = "meta";

/**
 * Bumping this **drops the database**. Deliberately not a migration path: the
 * contents are a pure cache of something re-derivable from two Range requests,
 * so throwing it away is always correct, always cheap, and always cheaper than
 * a migration that has to be right about data written by a version of the code
 * that no longer exists.
 */
export const SCHEMA_VERSION = 5;

/*
 * Version 5 renames `DtypeTally.params` to `storedElements`.
 *
 * The same rename as version 3, one level down, and for the same reason: the
 * per-dtype ledger counts what a dtype physically holds, which is not a
 * parameter count and must not be called one. Non-parameter buffers made that
 * gap visible on a checkpoint with no packing anywhere — `openai-community/gpt2`
 * puts 137M elements against 124,439,808 parameters — so the column that used
 * to agree with the header by coincidence now openly disagrees with it, and had
 * to say what it actually measures.
 *
 * Bumped because a version-4 entry's tallies carry `params` and no
 * `storedElements`. A reader of one finds `undefined`, which is not a loud
 * failure: `formatParams(undefined)` prints an em dash and `bytes * 8 /
 * undefined` prints **NaN**, so the ledger renders with a dash where its count
 * belongs and `NaN` in its bits-per-weight column — for exactly the visitors who
 * had looked at something before. Observed in dev before this constant moved,
 * which is the second time this file's own note has been proven by ignoring it.
 */

/*
 * Version 4 adds `fileProvenance` — what a GGUF header declares about the model
 * it was made from (T0(d)).
 *
 * A version-3 entry has no such field, and `undefined` there is indistinguishable
 * from "this file declares no parent". Serving one would mean a GGUF that *does*
 * declare its base model silently offering no compare suggestion, for exactly
 * the visitors who had looked at it before — the ones most likely to come back.
 *
 * Note what is *not* stored: the header map these values come from. Its key set
 * is whatever the parser's `KEEP_PREFIXES` retains, so storing it would make
 * every cached entry's shape follow a list maintained for unrelated reasons.
 * See `Inventory.fileProvenance`.
 */

/*
 * Version 3 renames `totalParams` to `storedElements`.
 *
 * Not a new field — the same number under an honest name. It never was a
 * parameter count for a packed repo, and being called one is how the status
 * line came to report 11.96B for `openai/gpt-oss-20b`. A reader of a version-2
 * entry would find no `storedElements`, so the constant moves in the same
 * commit, exactly as the note below demands.
 *
 * The cost is one cold load per visitor who had already looked at something.
 * That is the trade this constant exists to make cheap.
 */

/*
 * Version 2 adds `component` to every stored inventory (multi-component repos).
 *
 * Bumped because an entry written by version 1 has no such field, and a reader
 * that assumed it did threw `Cannot read properties of undefined (reading
 * 'replace')` on any repo the visitor had already looked at — reported from
 * production on `TheBloke/TinyLlama-1.1B-Chat-v0.3-GPTQ`, 2026-08-09, minutes
 * after the deploy.
 *
 * The lesson is not "add a field carefully". It is that this constant exists
 * precisely so a shape change costs one line, and the shape was changed without
 * touching it. Any future field added to `Inventory` needs this bumped in the
 * same commit.
 */

/**
 * The bound. Two limits, because either one alone has a bad case.
 *
 * A 290-tensor inventory is a few hundred KB; sharded frontier models carry
 * thousands of tensors and run to a couple of MB. 32 MiB therefore holds
 * roughly a dozen large models or many dozens of ordinary ones — enough that a
 * visitor stepping through a family of checkpoints keeps hitting cache — while
 * staying far below any browser's per-origin quota (typically hundreds of MB to
 * several GB). It should never be the reason a visitor is prompted about
 * storage, nor the reason some other site's data gets evicted.
 *
 * The entry cap exists because a byte bound alone would let a few thousand tiny
 * repos accumulate: each is cheap, but the metadata scan that eviction and
 * `inventoryCacheStats` perform is O(entries), and an unbounded index is how a
 * cache quietly becomes slower than the thing it replaced.
 */
const MAX_ENTRIES = 24;
const MAX_BYTES = 32 * 1024 * 1024;

/**
 * Any IndexedDB operation that has not settled in this long is treated as
 * unavailable.
 *
 * This is not belt-and-braces. A version upgrade blocked by another tab holding
 * the old database open fires `blocked` and then *nothing* — no success, no
 * error — until that tab goes away. Without a deadline `readCachedInventory`
 * would never resolve and `load()` would await it forever, turning a cache miss
 * into a hung page. That is the exact failure this module is not allowed to
 * have.
 *
 * The value is the worst-case latency this cache may *add* for a visitor whose
 * browser cannot serve it, paid once per session (the `unavailable` latch below
 * makes sure of that). A healthy open takes single-digit milliseconds and a
 * write of the largest inventory well under a hundred, so 1.5 s is far past
 * "slow" and comfortably short of "the page is broken".
 */
const OPERATION_TIMEOUT_MS = 1_500;

/**
 * NUL as the key separator, matching `worker/client.ts`: it cannot occur in a
 * repo id or a sha, so no combination of the two can collide. Written as an
 * escape and never as a literal byte — a raw NUL in source makes grep treat the
 * file as binary and report no matches at all (see scripts/check-no-nul.ts).
 */
const SEPARATOR = "\u0000";

/**
 * `repoId|sha`, plus the component when there is one.
 *
 * The component is appended only when non-empty, so every key written before
 * multi-component repos existed stays valid — a root-level model is still
 * `repoId|sha`. Without the extra segment two components of one repo share a
 * key and the second to load serves the first one's tensors, which looks like a
 * correct read of the wrong model.
 */
function entryKey(repoId: string, sha: string, component = ""): string {
  const base = `${repoId}${SEPARATOR}${sha}`;
  return component ? `${base}${SEPARATOR}${component}` : base;
}

/**
 * The per-entry bookkeeping, kept in its own object store.
 *
 * Separate from the inventory itself so that eviction and `inventoryCacheStats`
 * can read *all* of it — a few dozen tiny objects — without structured-cloning
 * tens of MB of tensor lists back out of storage just to learn which entry is
 * the oldest.
 */
interface MetaRecord {
  readonly key: string;
  readonly repoId: string;
  readonly sha: string;
  /** LRU stamp. Internal only; see `nextStamp`. */
  readonly lastUsed: number;
  readonly approxBytes: number;
}

/** Module state, all of it discardable. */
let schemaVersion = SCHEMA_VERSION;
let dbPromise: Promise<IDBDatabase | undefined> | undefined;
let unavailable = false;
let lastStamp = 0;

/**
 * A monotonically increasing LRU stamp, seeded from the wall clock.
 *
 * `Date.now()` is fine here — this is app code, and nothing derived from the
 * stamp is ever rendered, put in a URL, or read by a fixture. But it is only
 * millisecond-resolution, and several inventories can be written inside one
 * millisecond, which would leave the eviction order between them undefined.
 * Forcing the stamp forward keeps "least recently used" a total order whatever
 * the clock's resolution.
 */
function nextStamp(): number {
  lastStamp = Math.max(Date.now(), lastStamp + 1);
  return lastStamp;
}

/** One IDB request as a promise. */
function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error("IndexedDB request failed"));
  });
}

/**
 * Resolve `undefined` if `work` has not settled within the deadline, and on any
 * rejection.
 *
 * A timed-out operation is abandoned, not cancelled — IndexedDB has no cancel —
 * so it may still complete later against a handle nobody is holding. That is
 * harmless: the worst case is a write that lands after we stopped caring, and
 * what it writes is still keyed on an immutable SHA.
 */
function withDeadline<T>(work: Promise<T>): Promise<T | undefined> {
  return new Promise<T | undefined>((resolve) => {
    const timer = setTimeout(() => resolve(undefined), OPERATION_TIMEOUT_MS);
    const settle = (value: T | undefined): void => {
      clearTimeout(timer);
      resolve(value);
    };
    work.then(settle, () => settle(undefined));
  });
}

/**
 * Open the database, or give up quietly.
 *
 * Failure resolves `undefined` rather than rejecting, because to every caller
 * "no database" and "a miss" are the same instruction: read the headers.
 */
function openDatabase(): Promise<IDBDatabase | undefined> {
  const factory: IDBFactory | undefined = globalThis.indexedDB;
  if (!factory) return Promise.resolve(undefined);

  return new Promise<IDBDatabase | undefined>((resolve) => {
    let request: IDBOpenDBRequest;
    try {
      request = factory.open(DB_NAME, schemaVersion);
    } catch {
      // Some browsers throw here instead of firing `error` — Firefox in private
      // browsing has historically done both, depending on the version.
      resolve(undefined);
      return;
    }

    request.onupgradeneeded = () => {
      // Drop and recreate, never migrate. Anything already there was written by
      // a schema this build no longer understands, and every byte of it is
      // re-derivable from two Range requests.
      const db = request.result;
      try {
        for (const name of Array.from(db.objectStoreNames)) {
          db.deleteObjectStore(name);
        }
        db.createObjectStore(STORE_DATA);
        db.createObjectStore(STORE_META);
      } catch {
        // Aborting leaves the stored database untouched and surfaces as `error`
        // on the open request below, which is the "no cache" path.
        request.transaction?.abort();
      }
    };
    request.onsuccess = () => {
      const db = request.result;
      // If another tab later opens this database at a newer schema version, our
      // held-open handle is what would block it — and a blocked upgrade is the
      // hang described above, inflicted on that tab. Let go instead.
      db.onversionchange = () => {
        db.close();
        dbPromise = undefined;
      };
      resolve(db);
    };
    request.onerror = () => resolve(undefined);
    // Fired when *we* are the ones blocked by another tab. Treat it as "no
    // cache" at once rather than waiting out the deadline.
    request.onblocked = () => resolve(undefined);
  });
}

/**
 * The memoized handle, or `undefined` forever after the first failure.
 *
 * The latch is not just tidiness: the failure modes that hang cost a full
 * `OPERATION_TIMEOUT_MS` each, and paying that on every repo load would make
 * the cache a latency *tax* on exactly the visitors whose browsers cannot use
 * it.
 */
async function database(): Promise<IDBDatabase | undefined> {
  if (unavailable) return undefined;
  if (!dbPromise) dbPromise = withDeadline(openDatabase());
  const db = await dbPromise;
  if (!db) {
    unavailable = true;
    dbPromise = undefined;
  }
  return db;
}

/**
 * Run `work` in one transaction, resolving `undefined` if anything at all goes
 * wrong: a missing store, a request that errors, a quota-exceeded write, an
 * abort from any source, or a transaction that never commits.
 *
 * The value is only handed back once the transaction has *completed*. For a
 * write, returning from a transaction that later aborted would be a claim that
 * something was stored when it was not.
 */
async function withTransaction<T>(
  stores: readonly string[],
  mode: IDBTransactionMode,
  work: (tx: IDBTransaction) => Promise<T>,
): Promise<T | undefined> {
  const db = await database();
  if (!db) return undefined;

  const run = (async () => {
    // `transaction()` throws if a store is missing, which is what a database
    // left behind by a half-finished upgrade looks like.
    const tx = db.transaction([...stores], mode);
    const completed = new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onabort = () => reject(tx.error ?? new Error("IndexedDB transaction aborted"));
      tx.onerror = () => reject(tx.error ?? new Error("IndexedDB transaction failed"));
    });
    // If `work` rejects first, nothing would ever await `completed` and the
    // abort that follows would surface as an unhandled rejection — noise in a
    // console the visitor may well have open.
    completed.catch(() => undefined);

    const value = await work(tx);
    await completed;
    return value;
  })();

  return withDeadline(run);
}

/**
 * A generous over-estimate of an entry's stored size, for budgeting only.
 *
 * Deliberately arithmetic rather than `JSON.stringify(...).length`: measuring
 * exactly would mean allocating a multi-megabyte string on every repo load to
 * answer a question whose only consumer is a 32 MiB threshold. Over-estimating
 * is the safe direction — it evicts slightly early rather than overshooting.
 */
function estimateBytes(inventory: Inventory): number {
  // Repo identity plus the top-level scalars.
  let bytes = 256;
  for (const file of inventory.repo.files) bytes += file.length * 2 + 16;
  for (const file of inventory.files) bytes += file.length * 2 + 16;
  bytes += inventory.byDtype.length * 64;
  for (const tensor of inventory.tensors) {
    // 96 covers the fixed numeric fields (both relative offsets, numel,
    // byteLength, both absolute offsets), the dtype string, and per-object
    // overhead.
    bytes +=
      tensor.name.length * 2 + tensor.file.length * 2 + tensor.shape.length * 8 + 96;
  }
  return bytes;
}

/**
 * Look up a previously parsed inventory for this exact commit.
 *
 * Resolves `undefined` for a miss and for every failure, which are the same
 * thing to the caller: read the headers.
 */
export async function readCachedInventory(
  repoId: string,
  sha: string,
  component = "",
): Promise<Inventory | undefined> {
  if (!repoId || !sha) return undefined;
  const key = entryKey(repoId, sha, component);

  const stored = await withTransaction([STORE_DATA], "readonly", (tx) =>
    requestResult<unknown>(tx.objectStore(STORE_DATA).get(key)),
  );
  if (stored === undefined) return undefined;

  // A hit is a use, so restamp — but in its own transaction, so that a failure
  // to record the use cannot cost us a hit we already have in hand.
  await withTransaction([STORE_META], "readwrite", async (tx) => {
    const store = tx.objectStore(STORE_META);
    const meta = await requestResult<MetaRecord | undefined>(store.get(key));
    store.put(
      {
        key,
        repoId,
        sha,
        lastUsed: nextStamp(),
        approxBytes: meta?.approxBytes ?? estimateBytes(stored as Inventory),
      } satisfies MetaRecord,
      key,
    );
  });

  return stored as Inventory;
}

/**
 * Store an inventory under its own `(repoId, sha)`, evicting if that puts the
 * cache over either bound. Resolves either way; never throws.
 *
 * `Inventory` is plain data all the way down — strings, numbers, and arrays of
 * them — so it survives the structured clone algorithm unchanged and comes back
 * deeply equal, `repo: ResolvedRepo` included. No serializable projection or
 * rebuild step is needed. (`readonly` and the `[start, end]` tuple are
 * type-level only; the runtime shapes are ordinary arrays, which is why what
 * comes back can simply be asserted as `Inventory`.) A test holds this true, so
 * that adding a `Map`, a class instance or a function to `Inventory` fails here
 * rather than in a browser.
 */
export async function writeCachedInventory(inventory: Inventory): Promise<void> {
  const { repoId, sha } = inventory.repo;
  if (!repoId || !sha) return;
  const key = entryKey(repoId, sha, inventory.component);
  const approxBytes = estimateBytes(inventory);

  // An entry bigger than the entire budget would evict everything else and
  // still not fit. Skip it: re-reading one pathological repo's headers is
  // cheaper than emptying the cache of every other repo the visitor looks at.
  if (approxBytes > MAX_BYTES) return;

  await withTransaction([STORE_DATA, STORE_META], "readwrite", async (tx) => {
    const dataStore = tx.objectStore(STORE_DATA);
    const metaStore = tx.objectStore(STORE_META);
    dataStore.put(inventory, key);
    metaStore.put(
      { key, repoId, sha, lastUsed: nextStamp(), approxBytes } satisfies MetaRecord,
      key,
    );

    // Least-recently-used, evaluated on write only: a visit that hits cache
    // does no work here at all beyond its restamp.
    const all = await requestResult<MetaRecord[]>(metaStore.getAll());
    all.sort((a, b) => a.lastUsed - b.lastUsed);
    let entries = all.length;
    let bytes = all.reduce((sum, record) => sum + record.approxBytes, 0);
    for (const record of all) {
      if (entries <= MAX_ENTRIES && bytes <= MAX_BYTES) break;
      // The entry just written carries the highest stamp, so it sorts last and
      // is never the one evicted — unless it were alone and over budget, which
      // the size check above already ruled out.
      dataStore.delete(record.key);
      metaStore.delete(record.key);
      entries -= 1;
      bytes -= record.approxBytes;
    }
  });
}

/** Empty the cache. Resolves even if there was nothing to empty, or no cache. */
export async function clearInventoryCache(): Promise<void> {
  await withTransaction([STORE_DATA, STORE_META], "readwrite", async (tx) => {
    tx.objectStore(STORE_DATA).clear();
    tx.objectStore(STORE_META).clear();
  });
}

/**
 * What the cache is holding. Reports zeroes when it is unavailable, which is
 * the truth from the app's point of view.
 */
export async function inventoryCacheStats(): Promise<{
  entries: number;
  bytes: number;
}> {
  const stats = await withTransaction([STORE_META], "readonly", async (tx) => {
    const all = await requestResult<MetaRecord[]>(tx.objectStore(STORE_META).getAll());
    return {
      entries: all.length,
      bytes: all.reduce((sum, record) => sum + record.approxBytes, 0),
    };
  });
  return stats ?? { entries: 0, bytes: 0 };
}

/** Drop the memoized handle and the "gave up" latch. Tests only. */
export function resetInventoryCacheForTests(): void {
  const held = dbPromise;
  void held?.then((db) => db?.close()).catch(() => undefined);
  dbPromise = undefined;
  unavailable = false;
  schemaVersion = SCHEMA_VERSION;
}

/** Pretend to be a build with a different schema version. Tests only. */
export function setSchemaVersionForTests(version: number): void {
  schemaVersion = version;
}
